import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { ENV } from "@app/constants/env";
import { getToken } from "@app/helpers/authProvider";

type Recipient = {
  RecipientsId: number;
  Recipients: string;
  PreAlertID: number;
  UserId: number;
};

type ApiResponse<T> = {
  data: T;
  success: boolean;
  message?: string;
};

const API_BASE_URL = ENV.BASE_API_URL +ENV.JOB_APIURL;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
});

const authBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (incomingArgs, api, extraOptions) => {
  let args: string | FetchArgs = incomingArgs;

  if (typeof args === "string") {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      const headers =
        (args as FetchArgs).headers instanceof Headers
          ? (args as FetchArgs).headers
          : new Headers((args as FetchArgs).headers);

      headers.set("Authorization", `Bearer ${token}`);
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // refresh token flow could go here
    }

    return result;
  } catch {
    return {
      error: {
        status: 500,
        data: "Token retrieval failed",
      },
    } as FetchBaseQueryError;
  }
};

// Normalizes API responses that may be wrapped with a { data, success } envelope
function normalizeApiResponse<T>(response: any): ApiResponse<T> {
  if (response && typeof response === "object" && "data" in response) {
    return response as ApiResponse<T>;
  }
  return { data: response as T, success: true };
}

export const recipientApiSlice = createApi({
  reducerPath: "efairsJobRecipientApi",
  baseQuery: authBaseQuery,
  tagTypes: ["Recipient"],
  endpoints: (builder) => ({
    getAllRecipients: builder.mutation<
      ApiResponse<Recipient[]>,
      { preAlertId: number }
    >({
      query: ({ preAlertId }) => ({
        url: `/Recipients/GetAllRecipients?preAlertId=${preAlertId}`,
        method: "POST",
      }),
      invalidatesTags: ["Recipient"],
      transformResponse: (response: any) => normalizeApiResponse<Recipient[]>(response),
    }),
    insertRecipients: builder.mutation<
      ApiResponse<Recipient>,
      Recipient
    >({
      query: (recipientBody) => ({
        url: "/Recipients/InsertRecipients",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: recipientBody,
      }),
      invalidatesTags: ["Recipient"],
      transformResponse: (response: any) => normalizeApiResponse<Recipient>(response),
    }),
    updateRecipients: builder.mutation<
      ApiResponse<Recipient>,
      { id: number; recipientBody: Recipient }
    >({
      query: ({ id, recipientBody }) => ({
        url: `/Recipients/UpdateRecipients/${id}`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: recipientBody,
      }),
      invalidatesTags: ["Recipient"],
      transformResponse: (response: any) => normalizeApiResponse<Recipient>(response),
    }),
    deleteRecipients: builder.mutation<
      ApiResponse<void>,
      { id: number; preAlertId: number; userId: number }
    >({
      query: ({ id, preAlertId, userId }) => ({
        url: `/Recipients/DeleteRecipients/${id}/${preAlertId}?userId=${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Recipient"],
    }),
  }),
});

export const {
  useGetAllRecipientsMutation,
  useInsertRecipientsMutation,
  useUpdateRecipientsMutation,
  useDeleteRecipientsMutation,
} = recipientApiSlice;
